import { handleAxiosError } from '@/api/helpers';
import { client } from '@/client';
import { Course } from '@/client/courses';
import { Actions, Subjects, SystemRoles } from '@/client/users';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { DialogContext } from '@/common/context';
import { useAppSelector } from '@/hooks/store';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { usePermission } from '@/hooks/usePermission';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account/current-account.slice';
import { selectCurrentUser } from '@/store/features/users/current-user.slice';
import { DialogType } from '@/system-settings/enums';
import {
  branchAdminCheck,
  CloneIcon,
  CustomizableIcon,
  determineCourseExportLanguage,
  isContentDeletableByStatus,
  isDemoRestrictedMode,
} from '@/utils/helpers';
import { PostHogFeatureFlags } from '@/utils/posthog';
import { AxiosError } from 'axios';
import { MenuItem } from 'primereact/menuitem';
import { Tooltip } from 'primereact/tooltip';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

type CourseRowMenuHandlers = {
  onDelete: (course: Course) => Promise<void> | void;
  onClone: (course: Course) => void;
  onSubmitClone: (
    targetAccountId: string,
    targetAccountName?: string,
    course?: Course,
  ) => Promise<void> | void;
};

export const useCourseRowMenu = ({
  onDelete,
  onClone,
  onSubmitClone,
}: CourseRowMenuHandlers) => {
  const { t } = useTranslation();
  const { can, isRestrictedByRole } = usePermission();
  const isCreateRestricted = isRestrictedByRole(
    Actions.CREATE,
    Subjects.COURSES,
  );
  const toast = useToast();
  const navigate = useNavigate();
  const account = useAppSelector(selectCurrentAccount);
  const currentUser = useAppSelector(selectCurrentUser);
  const isAdminUser = currentUser?.role.code === SystemRoles.ADMIN;
  const isDemoAccount = isDemoRestrictedMode(account, currentUser);
  const isBranchAdmin = branchAdminCheck(currentUser, account);
  const { setDialogData } = useContext(DialogContext);
  const { canUseFeature } = useFeatureFlag();

  const menuItems = (course: Course) => {
    const menu: MenuItem[] = [
      {
        label: t('course.view.content'),
        icon: 'pi pi-pencil',
        command: () => {
          navigate(
            RedirectPaths[RedirectPathsEnum.COURSES_SCORM_PREVIEW](course.id),
          );
        },
      },
    ];

    if (account?.isSystem) {
      menu.push({
        label: t('course.view.schedule'),
        icon: 'pi pi-calendar',
        command: () => {
          navigate(
            RedirectPaths[RedirectPathsEnum.COURSES_SCHEDULE](course.id),
          );
        },
      });

      if (can(Actions.READ, Subjects.COURSE_ACCOUNT_SCHEDULES)) {
        menu.push({
          label: t('course.view.accounts'),
          icon: 'pi pi-building',
          command: () => {
            navigate(
              RedirectPaths[RedirectPathsEnum.COURSES_ACCOUNTS](course.id),
            );
          },
        });
      }
    }

    if (
      can(Actions.READ, Subjects.COURSE_USER_SCHEDULES) &&
      !account?.isSystem
    ) {
      menu.push({
        label: t('course.view.users'),
        icon: 'pi pi-user',
        command: () => {
          navigate(RedirectPaths[RedirectPathsEnum.COURSES_USERS](course.id));
        },
      });
    }

    if (
      course?.cloneSettings?.isCloneable &&
      (can(Actions.CREATE, Subjects.COURSES) || isCreateRestricted)
    ) {
      menu.push({
        label: t('content.clone'),
        icon: <CloneIcon />,
        disabled: isCreateRestricted,
        command: async () => {
          if (isCreateRestricted) return;

          if (isAdminUser && account?.id) {
            await onSubmitClone(account.id, account.name, course);
          } else {
            onClone(course);
          }
        },
      });
    }

    const isCourseAiCustomized =
      !!course?.customizationMetadata?.originalContentId;
    const isCourseCloned = !!course?.cloneMetadata?.isClone;

    if (
      !account?.isSystem &&
      course?.aiCustomizationSettings?.isEnabled &&
      !isCourseAiCustomized &&
      !isCourseCloned
    ) {
      menu.push({
        label: t('content.customizeWithAi'),
        icon: <CustomizableIcon />,
        disabled: isCreateRestricted,
        command: () => {
          if (isCreateRestricted) return;

          navigate(
            RedirectPaths[RedirectPathsEnum.COURSES_CUSTOMIZE](course.id),
          );
        },
      });
    }

    if (canUseFeature(PostHogFeatureFlags.EXPORT_TO_LMS)) {
      if (course.hasScormContent) {
        const disabledClass = `scorm-export-disabled-${course.id}`;
        menu.push({
          label: t('courses.export.to.lms'),
          template: (
            _item: MenuItem,
            options: {
              className: string;
              iconClassName: string;
              labelClassName: string;
            },
          ) => (
            <>
              <span
                className={`${options.className} ${disabledClass}`}
                style={{
                  cursor: 'not-allowed',
                  opacity: 0.5,
                  pointerEvents: 'all',
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <span
                  className={`pi pi-download ${options.iconClassName}`}
                  style={{ marginRight: '0.5rem' }}
                />
                <span className={options.labelClassName}>
                  {t('courses.export.to.lms')}
                </span>
              </span>
              <Tooltip
                target={`.${disabledClass}`}
                content={t('courses.export.to.lms.scorm.not.supported')}
                position="left"
              />
            </>
          ),
        });
      } else {
        menu.push({
          label: t('courses.export.to.lms'),
          icon: 'pi pi-download',
          command: async () => {
            try {
              const response = await client.courses.exportCourseAsScorm(
                course.id,
                determineCourseExportLanguage(course),
                account?.id as string,
              );
              window.open(
                response.downloadUrl,
                '_blank',
                'noopener,noreferrer',
              );
            } catch (error) {
              handleAxiosError(error as Error | AxiosError, toast);
            }
          },
        });
      }
    }

    const isCustomCourse = !!course.account;
    const isSystemView = !!account?.isSystem;
    const canDeleteInAccountContext = isSystemView || isCustomCourse;
    const canDeleteByStatus = isContentDeletableByStatus({
      status: course.status,
      isCustomCourse,
    });

    const canDelete =
      canDeleteByStatus &&
      !isBranchAdmin &&
      can(Actions.DELETE, Subjects.COURSES) &&
      canDeleteInAccountContext;

    if (canDelete) {
      menu.push({
        label: t('generic.delete'),
        icon: 'pi pi-trash',
        command: isDemoAccount
          ? undefined
          : () =>
              setDialogData({
                type: DialogType.CONFIRMATION,
                show: true,
                header: t('dialog.delete.confirm'),
                message: t('dialog.delete.content'),
                onAccept: async () => onDelete(course),
              }),
      });
    }

    return menu;
  };

  return menuItems;
};
